import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { getOrgBudget, setOrgBudget } from "../api/budgetApi";
import { BUDGET_ORGANIZATIONS } from "../constants/organizations";
import { formatInr } from "../utils/formatInr";

const fieldStyle = {
  width: "100%",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  padding: "8px 10px",
  fontSize: "0.88rem",
};

const btn = (bg, color = "#fff") => ({
  border: "none",
  borderRadius: 8,
  padding: "8px 14px",
  background: bg,
  color,
  fontWeight: 700,
  fontSize: "0.82rem",
  cursor: "pointer",
});

export default function BudgetOrgBudgetPanel({ budgetYear, organizationView, onSaved }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["budget-org-budget", budgetYear, organizationView],
    queryFn: () =>
      getOrgBudget({ year: budgetYear, organizationView: organizationView || undefined }).then((r) => r.data),
    staleTime: 0,
  });

  useEffect(() => {
    if (data?.orgBudget) {
      setAmount(String(data.orgBudget.totalAmountInr ?? ""));
      setNotes(data.orgBudget.notes || "");
    } else {
      setAmount("");
      setNotes("");
    }
  }, [data?.orgBudget?.id, data?.orgBudget?.totalAmountInr, data?.orgBudget?.notes]);

  const saveMut = useMutation({
    mutationFn: (body) => setOrgBudget(body).then((r) => r.data),
    onSuccess: () => {
      toast.success("Organisation budget saved");
      qc.invalidateQueries({ queryKey: ["budget-org-budget"] });
      qc.invalidateQueries({ queryKey: ["budget-summary"] });
      onSaved?.();
    },
    onError: (e) => toast.error(e.response?.data?.message || "Save failed"),
  });

  const orgLabel = organizationView || "All organisations";

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #EAF6FD 0%, #f8fafc 100%)",
        border: "1px solid #a9ddf5",
        borderRadius: 14,
        padding: "16px 18px",
        marginBottom: 16,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 800, color: "#2C63AE", fontSize: "0.95rem" }}>
            Organisation budget ceiling
          </div>
          <div style={{ fontSize: "0.78rem", color: "#475569", marginTop: 4 }}>
            {budgetYear} · {orgLabel} — total annual budget for all departments
          </div>
        </div>
        {data?.committedAmountInr != null ? (
          <div style={{ fontSize: "0.78rem", color: "#64748b", textAlign: "right" }}>
            <div>Committed: <strong>{formatInr(data.committedAmountInr)}</strong></div>
            {data.remainingAmountInr != null ? (
              <div style={{ color: "#0f766e", fontWeight: 700 }}>Remaining: {formatInr(data.remainingAmountInr)}</div>
            ) : null}
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <div style={{ marginTop: 12, color: "#64748b", fontSize: "0.85rem" }}>Loading…</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginTop: 14 }}>
          <div>
            <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", marginBottom: 4 }}>
              Total org budget (INR) *
            </label>
            <input
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 50000000"
              style={fieldStyle}
            />
          </div>
          <div>
            <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", marginBottom: 4 }}>
              Notes
            </label>
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional context"
              style={fieldStyle}
            />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <button
              type="button"
              style={btn("#2C63AE")}
              disabled={!amount || Number(amount) < 0 || saveMut.isPending}
              onClick={() =>
                saveMut.mutate({
                  budgetYear,
                  organization: organizationView || undefined,
                  totalAmountInr: Number(amount),
                  notes: notes.trim() || undefined,
                })
              }
            >
              Save org budget
            </button>
          </div>
        </div>
      )}

      {!organizationView ? (
        <div style={{ marginTop: 10, fontSize: "0.72rem", color: "#64748b" }}>
          Tip: filter by organisation ({BUDGET_ORGANIZATIONS.join(", ")}) to set per-entity ceilings.
        </div>
      ) : null}
    </div>
  );
}
